import { useLayoutEffect, useRef, useState } from 'react';
import { Dropdown } from '@quintoandar-tokko/jopijs';
import styles from './JopiDropdownItems.module.scss';

// jopijs structure: root Box (relative) > [Button trigger, List panel].
// Measuring the root would include the panel itself, so after flipping up the
// root's top shrinks and the next measurement wrongly decides to drop down.
const getTrigger = (panel) => {
  if (!panel?.parentElement) return null;

  return (
    panel.previousElementSibling ||
    Array.from(panel.parentElement.children).find((child) => child !== panel) ||
    panel.parentElement
  );
};

const isOverlay = (element) => {
  const { position } = window.getComputedStyle(element);
  return position === 'fixed' || position === 'sticky';
};

// Ancestors that clip their content hide anything drawn above their top edge.
const getClippingTop = (trigger) => {
  let limit = 0;
  let node = trigger.parentElement;

  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY && overflowY !== 'visible') {
      limit = Math.max(limit, node.getBoundingClientRect().top);
    }
    node = node.parentElement;
  }

  return limit;
};

// Fixed/sticky chrome (e.g. the app header) painted over the point where the
// flipped panel would start; the panel would end up hidden behind it.
const getOverlayBottomAt = (x, y, trigger) => {
  if (typeof document.elementsFromPoint !== 'function') return 0;

  return document
    .elementsFromPoint(x, Math.max(y, 0))
    .reduce(
      (limit, element) =>
        element.contains(trigger) || !isOverlay(element)
          ? limit
          : Math.max(limit, element.getBoundingClientRect().bottom),
      0
    );
};

// Decide placement from the trigger button and the panel height only. Do not
// use the root box rect — it changes when the panel flips and causes oscillation.
// When the panel fits in neither direction it opens downwards so the user can
// scroll the page to reach it, instead of leaving it hidden above.
export const shouldDropUp = (anchor) => {
  const panel = anchor?.parentElement;
  const trigger = getTrigger(panel);

  if (!panel || !trigger) return false;

  const { top, bottom, left, right } = trigger.getBoundingClientRect();
  const panelHeight = panel.offsetHeight;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;

  if (panelHeight <= viewportHeight - bottom) return false;

  const visibleTop = Math.max(
    getClippingTop(trigger),
    getOverlayBottomAt((left + right) / 2, top - panelHeight, trigger)
  );

  return panelHeight < top - visibleTop;
};

// jopijs only renders `Dropdown.Items` children while the panel is open, so
// mounting the effects here (instead of in the wrapper) ties them to the open
// state and lets us measure the panel once it is in the DOM.
function DropdownPanelEffects({ onPlacementChange, onOpenChange }) {
  const anchorRef = useRef(null);
  const onOpenChangeRef = useRef(onOpenChange);
  const onPlacementChangeRef = useRef(onPlacementChange);
  onOpenChangeRef.current = onOpenChange;
  onPlacementChangeRef.current = onPlacementChange;

  useLayoutEffect(() => {
    onOpenChangeRef.current?.(true);
    onPlacementChangeRef.current(shouldDropUp(anchorRef.current));

    return () => {
      // Reset so the next open does not start with a stale dropUp class
      // (JopiDropdownItems stays mounted while jopi hides the List).
      onPlacementChangeRef.current(false);
      onOpenChangeRef.current?.(false);
    };
  }, []);

  return <span ref={anchorRef} style={{ display: 'none' }} aria-hidden />;
}

function JopiDropdownItems({
  children,
  className,
  clickClose = true,
  onOpenChange,
  forceDropDown = false,
  ...props
}) {
  const [dropUp, setDropUp] = useState(false);
  const itemsClassName =
    [className, !forceDropDown && dropUp && styles.dropUp]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <Dropdown.Items
      clickClose={clickClose}
      className={itemsClassName}
      data-testid="jopi-dropdown-items"
      {...props}
    >
      {children}
      {/* Keep the anchor after children so consumer `> div:first-child`
          rules (SearchableMultiSelect / FilterBar) still match. */}
      {forceDropDown ? (
        <DropdownPanelEffects
          onPlacementChange={() => {}}
          onOpenChange={onOpenChange}
        />
      ) : (
        <DropdownPanelEffects
          onPlacementChange={setDropUp}
          onOpenChange={onOpenChange}
        />
      )}
    </Dropdown.Items>
  );
}

export default JopiDropdownItems;
